import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Layout,
  Card,
  Typography,
  Spin,
  Alert,
  Result,
  Button,
  Space,
  Tag,
  Image,
} from "antd";
import {
  StarOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import ReviewForm from "./components/ReviewForm";
import API_CONFIG from "../config/api";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

/**
 * Page dédiée pour laisser un avis client
 *
 * Accessible via lien email avec token JWT
 * URL: /review?token=xxx&product_id=123&order_id=456
 */
const ReviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Paramètres URL
  const reviewToken = searchParams.get("token");
  const productId = searchParams.get("product_id");
  const orderId = searchParams.get("order_id");

  // États
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productData, setProductData] = useState<any>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [submitted, setSubmitted] = useState(false);
  const [storeSlug, setStoreSlug] = useState<string>("");

  /**
   * Vérifier le token et charger les données produit/commande
   */
  useEffect(() => {
    const validateAndLoadData = async () => {
      // Vérification des paramètres requis
      if (!reviewToken || !productId || !orderId) {
        setError("Lien invalide. Paramètres manquants.");
        setLoading(false);
        return;
      }

      try {
        // Appel API pour vérifier le token et récupérer les données
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/customer/reviews/validate-token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              review_token: reviewToken,
              product_id: parseInt(productId),
              order_id: parseInt(orderId),
            }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Token invalide ou expiré");
        }

        // Stocker les données
        setProductData(data.product);
        setOrderData(data.order);
        setStoreSlug(data.store_slug || "");
        setLoading(false);
      } catch (err: any) {
        console.error("Erreur validation token:", err);
        setError(err.message || "Impossible de valider le lien");
        setLoading(false);
      }
    };

    validateAndLoadData();
  }, [reviewToken, productId, orderId]);

  /**
   * Callback succès après soumission avis
   */
  const handleSuccess = () => {
    setSubmitted(true);
  };

  /**
   * Affichage pendant le chargement
   */
  if (loading) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
        <Content
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "50px",
          }}
        >
          <Card style={{ maxWidth: 500, textAlign: "center" }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: 20 }}>
              Vérification de votre lien...
            </Paragraph>
          </Card>
        </Content>
      </Layout>
    );
  }

  /**
   * Affichage en cas d'erreur
   */
  if (error) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
        <Content
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <Card style={{ maxWidth: 600 }}>
            <Result
              status="error"
              title="Lien invalide ou expiré"
              subTitle={error}
              extra={[
                <Button
                  type="primary"
                  key="store"
                  icon={<ShoppingOutlined />}
                  onClick={() => navigate(storeSlug ? `/store/${storeSlug}` : "/")}
                >
                  Retour à la boutique
                </Button>,
              ]}
            />
          </Card>
        </Content>
      </Layout>
    );
  }

  /**
   * Affichage après soumission réussie
   */
  if (submitted) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
        <Content
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <Card style={{ maxWidth: 600 }}>
            <Result
              status="success"
              icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              title="Merci pour votre avis !"
              subTitle="Votre avis a été soumis avec succès. Il sera visible après validation par notre équipe."
              extra={[
                <Button
                  type="primary"
                  key="store"
                  icon={<ShoppingOutlined />}
                  onClick={() => navigate(`/store/${storeSlug}`)}
                >
                  Retour à la boutique
                </Button>,
                <Button
                  key="home"
                  icon={<HomeOutlined />}
                  onClick={() => navigate("/")}
                >
                  Accueil
                </Button>,
              ]}
            >
              <Paragraph>
                Nous apprécions votre retour et le partagerons avec d'autres clients
                pour les aider dans leurs choix.
              </Paragraph>
            </Result>
          </Card>
        </Content>
      </Layout>
    );
  }

  /**
   * Affichage du formulaire d'avis
   */
  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "20px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          {/* En-tête avec info produit */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div style={{ textAlign: "center" }}>
                <StarOutlined style={{ fontSize: 48, color: "#faad14" }} />
                <Title level={2} style={{ marginTop: 16, marginBottom: 8 }}>
                  Partagez votre expérience
                </Title>
                <Text type="secondary">
                  Votre avis aide les autres clients à faire le bon choix
                </Text>
              </div>

              {/* Info commande */}
              {orderData && (
                <Alert
                  message={
                    <Space>
                      <Text strong>Commande #{orderData.order_number}</Text>
                      <Tag color="success">Livrée</Tag>
                    </Space>
                  }
                  description={`Livrée le ${new Date(
                    orderData.delivered_at
                  ).toLocaleDateString("fr-FR")}`}
                  type="info"
                  showIcon
                />
              )}

              {/* Info produit */}
              {productData && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px",
                    background: "#fafafa",
                    borderRadius: "8px",
                  }}
                >
                  {productData.image_url && (
                    <Image
                      src={productData.image_url}
                      alt={productData.name}
                      width={80}
                      height={80}
                      style={{
                        objectFit: "cover",
                        borderRadius: "8px",
                        marginRight: "16px",
                      }}
                      preview={false}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <Title level={4} style={{ margin: 0 }}>
                      {productData.name}
                    </Title>
                    {productData.variant_name && (
                      <Text type="secondary">{productData.variant_name}</Text>
                    )}
                  </div>
                </div>
              )}
            </Space>
          </Card>

          {/* Formulaire d'avis */}
          <Card>
            <ReviewForm
              productId={parseInt(productId!)}
              orderId={parseInt(orderId!)}
              productName={productData?.name || "Ce produit"}
              customerToken={reviewToken!}
              onSuccess={handleSuccess}
              onCancel={() => navigate(storeSlug ? `/store/${storeSlug}` : "/")}
            />
          </Card>

          {/* Note de confidentialité */}
          <Card style={{ marginTop: 16, background: "#f9f9f9" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <strong>Confidentialité :</strong> Votre nom et email ne seront pas
              affichés publiquement. Seul votre prénom et la première lettre de
              votre nom seront visibles (ex: Marie L.). Les avis sont modérés
              avant publication pour garantir leur authenticité.
            </Text>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default ReviewPage;
